/**
 * Request Cache Manager
 * Implements intelligent caching and request deduplication for async operations
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  requestKey: string;
}

interface PendingRequest<T> {
  promise: Promise<T>;
  requestKey: string;
}

export class RequestCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private pendingRequests: Map<string, PendingRequest<any>> = new Map();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default

  /**
   * Generate cache key from parameters
   */
  private generateKey(prefix: string, params: Record<string, any>): string {
    const sorted = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {} as Record<string, any>);
    return `${prefix}:${JSON.stringify(sorted)}`;
  }

  /**
   * Check if cache entry is still valid
   */
  private isValid(entry: CacheEntry<any>, ttl: number = this.DEFAULT_TTL): boolean {
    return Date.now() - entry.timestamp < ttl;
  }

  /**
   * Get cached data if valid, otherwise return null
   */
  get<T>(prefix: string, params: Record<string, any>, ttl?: number): T | null {
    const key = this.generateKey(prefix, params);
    const entry = this.cache.get(key);

    if (entry && this.isValid(entry, ttl)) {
      return entry.data as T;
    }

    // Remove stale cache
    if (entry && !this.isValid(entry, ttl)) {
      this.cache.delete(key);
    }

    return null;
  }

  /**
   * Set cache data with timestamp
   */
  set<T>(prefix: string, params: Record<string, any>, data: T): void {
    const key = this.generateKey(prefix, params);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      requestKey: key
    });
  }

  /**
   * Deduplicate identical requests
   * Returns cached promise if same request is in flight
   */
  async deduplicateRequest<T>(
    prefix: string,
    params: Record<string, any>,
    requestFn: () => Promise<T>
  ): Promise<T> {
    const key = this.generateKey(prefix, params);

    // Check if identical request is already in flight
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!.promise;
    }

    // Check cache first
    const cached = this.get<T>(prefix, params);
    if (cached !== null) {
      return cached;
    }

    // Create new request and always clear pending state, including on failure.
    const promise = requestFn()
      .then((result) => {
        this.set(prefix, params, result);
        return result;
      })
      .finally(() => {
        this.pendingRequests.delete(key);
      });

    // Track pending request for deduplication
    this.pendingRequests.set(key, { promise, requestKey: key });

    return promise;
  }

  /**
   * Clear specific cache
   */
  clear(prefix: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(`${prefix}:`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => this.cache.delete(key));
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Get cache stats for debugging
   */
  getStats(): {
    cacheSize: number;
    pendingRequests: number;
  } {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size
    };
  }
}

// Singleton instance
export const requestCache = new RequestCache();
